import {
  CheckStatus,
  OrderItemStatus,
  OrderStatus,
  PaymentStatus,
  PurchaseOrderStatus,
  ReservationStatus,
  ResourceSessionStatus,
  ServiceRequestStatus,
  SubscriptionStatus,
  TableState,
  TakeawayStatus,
} from '@/lib/shared-types';

type Transitions<T extends string> = Record<T, readonly T[]>;

/** §31 — every allowed transition, in one place, used by server and clients. */
export const ORDER_TRANSITIONS: Transitions<OrderStatus> = {
  DRAFT: ['CONFIRMED', 'CANCELLED'],
  CONFIRMED: ['SENT_TO_KITCHEN', 'CANCELLED', 'VOIDED'],
  SENT_TO_KITCHEN: ['PREPARING', 'CANCELLED', 'VOIDED'],
  PREPARING: ['READY', 'CANCELLED', 'VOIDED'],
  READY: ['SERVED', 'COMPLETED', 'VOIDED'],
  SERVED: ['COMPLETED', 'REFUNDED'],
  COMPLETED: ['REFUNDED'],
  CANCELLED: [],
  VOIDED: [],
  REFUNDED: [],
};

export const TAKEAWAY_TRANSITIONS: Transitions<TakeawayStatus> = {
  DRAFT: ['SCHEDULED', 'RELEASED', 'CANCELLED'],
  SCHEDULED: ['RELEASED', 'CANCELLED'],
  RELEASED: ['PREPARING', 'CANCELLED'],
  PREPARING: ['READY', 'CANCELLED'],
  READY: ['HANDED_OVER', 'CANCELLED'],
  HANDED_OVER: ['COMPLETED', 'REFUNDED'],
  COMPLETED: ['REFUNDED'],
  CANCELLED: [],
  REFUNDED: [],
};

/** §15.2 — READY does NOT imply SERVED; pickup is its own step. */
export const ORDER_ITEM_TRANSITIONS: Transitions<OrderItemStatus> = {
  PENDING: ['QUEUED', 'CANCELLED'],
  QUEUED: ['PREPARING', 'CANCELLED'],
  PREPARING: ['READY', 'CANCELLED'],
  READY: ['AWAITING_PICKUP', 'SERVED', 'CANCELLED'],
  AWAITING_PICKUP: ['PICKED_UP', 'CANCELLED'],
  PICKED_UP: ['SERVED'],
  SERVED: [],
  CANCELLED: [],
};

export const TABLE_TRANSITIONS: Transitions<TableState> = {
  AVAILABLE: ['OCCUPIED', 'RESERVED', 'BLOCKED', 'OUT_OF_SERVICE'],
  OCCUPIED: ['PAYMENT_PENDING', 'CLEANING', 'AVAILABLE'],
  PAYMENT_PENDING: ['CLEANING', 'OCCUPIED', 'AVAILABLE'],
  CLEANING: ['AVAILABLE'],
  RESERVED: ['OCCUPIED', 'AVAILABLE', 'BLOCKED'],
  BLOCKED: ['AVAILABLE', 'OUT_OF_SERVICE'],
  OUT_OF_SERVICE: ['AVAILABLE'],
};

export const RESOURCE_SESSION_TRANSITIONS: Transitions<ResourceSessionStatus> = {
  SCHEDULED: ['ACTIVE', 'CANCELLED'],
  ACTIVE: ['PAUSED', 'ENDED'],
  PAUSED: ['ACTIVE', 'ENDED'],
  ENDED: [],
  CANCELLED: [],
};

export const CHECK_TRANSITIONS: Transitions<CheckStatus> = {
  OPEN: ['PARTIALLY_PAID', 'PAID', 'VOIDED'],
  PARTIALLY_PAID: ['PAID', 'VOIDED'],
  PAID: ['CLOSED', 'REFUNDED'],
  CLOSED: ['REFUNDED'],
  VOIDED: [],
  REFUNDED: [],
};

export const PAYMENT_TRANSITIONS: Transitions<PaymentStatus> = {
  PENDING: ['AUTHORIZED', 'PAID', 'FAILED'],
  AUTHORIZED: ['PAID', 'FAILED'],
  PAID: ['PARTIALLY_REFUNDED', 'REFUNDED'],
  FAILED: ['PENDING'],
  PARTIALLY_REFUNDED: ['REFUNDED'],
  REFUNDED: [],
};

/**
 * §35 — tenant subscription lifecycle. EXPIRED is not terminal: paying an overdue invoice revives
 * the tenant rather than forcing a new subscription. CANCELLED is, because a cancelled
 * subscription is replaced, never resumed.
 */
export const SUBSCRIPTION_TRANSITIONS: Transitions<SubscriptionStatus> = {
  TRIAL: ['ACTIVE', 'CANCELLED', 'EXPIRED'],
  ACTIVE: ['PAST_DUE', 'CANCELLED', 'EXPIRED'],
  PAST_DUE: ['ACTIVE', 'CANCELLED', 'EXPIRED'],
  CANCELLED: [],
  EXPIRED: ['ACTIVE'],
};

/**
 * §20 — reservation lifecycle. A booking may be seated straight from PENDING
 * (the guest turned up before anyone confirmed), and a seated booking can still
 * be cancelled because seating is reversible until the visit closes.
 */
export const RESERVATION_TRANSITIONS: Transitions<ReservationStatus> = {
  PENDING: ['CONFIRMED', 'SEATED', 'NO_SHOW', 'CANCELLED'],
  CONFIRMED: ['SEATED', 'NO_SHOW', 'CANCELLED'],
  SEATED: ['COMPLETED', 'CANCELLED'],
  NO_SHOW: [],
  CANCELLED: [],
  COMPLETED: [],
};

/**
 * §18 — service-request lifecycle. ESCALATED is a live state, not a dead end:
 * the SLA sweep raises a request into it and whoever picks it up moves it back
 * to ACCEPTED, so an escalation never strands the guest.
 */
export const SERVICE_REQUEST_TRANSITIONS: Transitions<ServiceRequestStatus> = {
  CREATED: ['ACCEPTED', 'ESCALATED', 'CANCELLED'],
  ACCEPTED: ['DONE', 'ESCALATED', 'CANCELLED'],
  ESCALATED: ['ACCEPTED', 'DONE', 'CANCELLED'],
  DONE: [],
  CANCELLED: [],
};

/**
 * §22 — purchase-order lifecycle: DRAFT → SUBMITTED → APPROVED →
 * PARTIALLY_RECEIVED → RECEIVED → CLOSED. SUBMITTED goes back to DRAFT so an
 * approver can return a document for correction, and a partially received order
 * may be CLOSED short when the balance is never coming.
 */
export const PURCHASE_ORDER_TRANSITIONS: Transitions<PurchaseOrderStatus> = {
  DRAFT: ['SUBMITTED', 'CANCELLED'],
  SUBMITTED: ['APPROVED', 'DRAFT', 'CANCELLED'],
  APPROVED: ['PARTIALLY_RECEIVED', 'RECEIVED', 'CANCELLED'],
  PARTIALLY_RECEIVED: ['RECEIVED', 'CLOSED', 'CANCELLED'],
  RECEIVED: ['CLOSED'],
  CANCELLED: [],
  CLOSED: [],
};

export function canTransition<T extends string>(
  machine: Transitions<T>,
  from: T,
  to: T,
): boolean {
  return machine[from]?.includes(to) ?? false;
}

export function assertTransition<T extends string>(
  machine: Transitions<T>,
  from: T,
  to: T,
  label = 'entity',
): void {
  if (!canTransition(machine, from, to)) {
    throw new Error(`Illegal ${label} transition: ${from} → ${to}`);
  }
}
